// Monitor tab — focused view of one strategy: KPI strip + chart + collapsible
// backtest + alert timeline. Empty state when nothing selected.

import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ChevronDown,
  Plus,
  AlertCircle,
  Monitor as MonitorIcon,
  ListChecks,
  FlaskConical,
} from "lucide-react";
import { cn } from "@/lib/utils";

import { useSelectedStrategy } from "../../state/SelectedStrategyContext";
import { useNavigation } from "../../state/NavigationContext";
import { getStrategy } from "../../api/strategies";
import { listAlerts } from "../../api/alerts";
import { fetchLatestCandles } from "../../api/candles";
import { getBacktestDetail, EMPTY_BACKTEST_ID } from "../../api/backtests";
import { useLiveCandles } from "../../hooks/useLiveCandles";
import { useTestEvents } from "../../hooks/useTestEvents";
import type {
  AlertSummaryDto,
  BacktestDetailDto,
  BacktestResponseDto,
  CandleDto,
  StrategyDto,
} from "../../api/types";
import { ChartPanel } from "../chart/ChartPanel";
import { AlertTimeline } from "../alerts/AlertTimeline";
import { BacktestPanel } from "../backtest/BacktestPanel";
import { MonitorKpiStrip } from "./MonitorKpiStrip";

const ALERT_POLL_MS = 4000;
const HISTORICAL_BARS = 500;
const RSI_PERIOD = 14;

export function MonitorPage() {
  const { selectedStrategyId } = useSelectedStrategy();
  const [strategy, setStrategy] = useState<StrategyDto | null>(null);
  const [candles, setCandles] = useState<CandleDto[]>([]);
  const [alerts, setAlerts] = useState<AlertSummaryDto[]>([]);
  const [backtest, setBacktest] = useState<BacktestResponseDto | null>(null);
  const [backtestDetail, setBacktestDetail] = useState<BacktestDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [backtestOpen, setBacktestOpen] = useState(true);

  // Lazy backtest-detail load — same pattern as before.
  useEffect(() => {
    const id = backtest?.backtestId;
    if (!id || id === EMPTY_BACKTEST_ID) { setBacktestDetail(null); return; }
    let cancelled = false;
    const ac = new AbortController();
    (async () => {
      try {
        const detail = await getBacktestDetail(id, ac.signal);
        if (!cancelled) setBacktestDetail(detail);
      } catch {
        if (!cancelled) setBacktestDetail(null);
      }
    })();
    return () => { cancelled = true; ac.abort(); };
  }, [backtest?.backtestId]);

  const { captures } = useTestEvents();
  const { navigateTo } = useNavigation();

  // Load the selected strategy on change.
  useEffect(() => {
    if (!selectedStrategyId) {
      setStrategy(null);
      setCandles([]);
      setAlerts([]);
      setBacktest(null);
      return;
    }
    let cancelled = false;
    setError(null); // clear any prior strategy's error so a healthy selection doesn't show the old error screen
    (async () => {
      try {
        const s = await getStrategy(selectedStrategyId);
        if (cancelled) return;
        setStrategy(s);
        const c = await fetchLatestCandles(s.spec.coin, s.spec.interval, s.spec.marketType, HISTORICAL_BARS);
        if (!cancelled) setCandles(c);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedStrategyId]);

  const onCandleClosed = useCallback((candle: CandleDto) => {
    setCandles((prev) => {
      const filtered = prev.filter((c) => c.openTime !== candle.openTime);
      return [...filtered, candle];
    });
  }, []);

  useLiveCandles(
    strategy?.spec.coin ?? null,
    strategy?.spec.interval ?? null,
    strategy?.spec.marketType ?? null,
    onCandleClosed,
  );

  // Poll alerts.
  useEffect(() => {
    if (!selectedStrategyId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const a = await listAlerts(selectedStrategyId);
        if (!cancelled) setAlerts(a);
      } catch { /* transient */ }
    };
    poll();
    const id = window.setInterval(poll, ALERT_POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [selectedStrategyId]);

  if (!selectedStrategyId) {
    return (
      <Card className="p-10 text-center bg-card-elevated max-w-2xl mx-auto mt-8">
        <MonitorIcon className="size-10 mx-auto mb-3 text-muted-foreground" />
        <h2 className="text-lg font-semibold tracking-tight mb-2">No strategy selected</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5 leading-relaxed">
          The Monitor tab focuses on one strategy at a time so the chart, alert feed,
          and backtest report all line up. Pick a strategy in the Strategies tab and
          click any card to select it.
        </p>
        <div className="flex justify-center gap-2">
          <Button
            onClick={() => navigateTo("strategies")}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <ListChecks className="size-4" />
            Go to Strategies
          </Button>
          <Button variant="outline" onClick={() => navigateTo("builder")}>
            <Plus className="size-4" />
            Create one
          </Button>
        </div>
      </Card>
    );
  }
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!strategy) {
    return <Card className="p-6 text-center text-muted-foreground text-sm bg-card-elevated">Loading…</Card>;
  }

  return (
    <div className="space-y-4">
      <MonitorKpiStrip strategy={strategy} alerts={alerts} />

      {/* Chart — fixed-height so it doesn't dominate when the page scrolls */}
      <div className="h-[520px]">
        <ChartPanel
          candles={candles}
          alerts={alerts}
          rsiPeriod={RSI_PERIOD}
          backtest={backtest}
          backtestDetail={backtestDetail}
        />
      </div>

      {/* Backtest panel — collapsible so the timeline can be the focus when you don't care */}
      <Collapsible open={backtestOpen} onOpenChange={setBacktestOpen}>
        <Card className="p-4 bg-card">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <FlaskConical className="size-4 text-primary" />
                <h3 className="text-sm font-semibold tracking-tight">Backtest</h3>
                <span className="text-xs text-muted-foreground">— evaluate the strategy on recent history</span>
              </div>
              <ChevronDown className={cn("size-4 text-muted-foreground transition-transform", backtestOpen && "rotate-180")} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3">
            {/* key by strategy id so switching strategies fully resets the panel's local result/
                error/persisted state — otherwise it shows the previous strategy's report (and could
                persist it onto the new id). */}
            <BacktestPanel key={strategy.id} strategy={strategy} onResult={setBacktest} />
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <AlertTimeline alerts={alerts} captures={captures} />
    </div>
  );
}
